import { Plan } from '../types/subscription'
import apiBase from '../utils/baseApi'

export interface GetStripeRecurringProps {
  token: string
}

export interface IGetStripeRecurringResponse {
  data: Plan[]
}

export async function getStripeRecurring(): Promise<IGetStripeRecurringResponse> {
  try {
    const response = await apiBase.get<IGetStripeRecurringResponse>(
      '/v1/get-stripe-recurring',
    )
    return response.data
  } catch (error) {
    console.error('Error getting subscriptions:', error)
    throw error
  }
}

export interface GetStripeRecurringCheckoutUrlResponse {
  data: {
    url: string
  }
}

export async function getStripeSubscriptionsCheckoutUrl(
  price_id: string,
  returnURL: string,
  cancelUrl: string,
): Promise<GetStripeRecurringCheckoutUrlResponse> {
  try {
    if (!returnURL) {
      returnURL = window.location.origin
    }
    const response = await apiBase.post<GetStripeRecurringCheckoutUrlResponse>(
      '/v2/stripe/subscriptions/checkout',
      {
        price_id,
        return_url: returnURL,
        cancel_url: cancelUrl,
      },
    )
    return response.data
  } catch (error) {
    console.error('Error getting checkout URL:', error)
    throw error
  }
}

export async function getStripeRecurringCheckoutUrl(
  price_id: string,
  returnURL?: string,
  cancelUrl?: string,
): Promise<GetStripeRecurringCheckoutUrlResponse> {
  try {
    if (!returnURL) {
      returnURL = window.location.origin
    }
    const response = await apiBase.post<GetStripeRecurringCheckoutUrlResponse>(
      '/v1/get-stripe-recurring-checkout-url',
      {
        price_id,
        return_url: returnURL,
        cancel_url: cancelUrl,
      },
    )
    return response.data
  } catch (error) {
    console.error('Error getting checkout URL:', error)
    throw error
  }
}

export interface IGetSubscriptionDetailsProps {
  organization_id: string
  persona_id?: string
}

export type BillingProvider =
  | 'none'
  | 'stripe'
  | 'recurly'
  | 'microsoft'
  | 'apple'
  | 'google'
export interface IGetSubscriptionDetailsResponse {
  success: boolean
  subscriptionStatus: string
  billingDetails: {
    planName: string
    paymentType: string
    billingPeriod: string
    price: number
    userConvertedToPaidAt?: string
    priceId: string
    provider: BillingProvider
  }
  credit_usage: {
    subscription_details: {
      balance: number
      total: number
      creditBalance: number
      creditsTotal: number
      status: string
    }
    pack_details: {
      balance: number
      total: number
      lastCheckoutAt: string | null
      creditBalance: number
      creditsTotal: number
    }
  }
}

export async function getSubscriptionDetails({
  organization_id,
  persona_id,
}: IGetSubscriptionDetailsProps): Promise<IGetSubscriptionDetailsResponse> {
  try {
    const params = new URLSearchParams({
      organization_id,
      ...(persona_id && { persona_id }),
    }).toString()
    const response = await apiBase.get<IGetSubscriptionDetailsResponse>(
      `/v1/get-subscription-details?${params}`,
    )
    return response.data
  } catch (error) {
    console.error('Error getting subscription details:', error)
    throw error
  }
}

export interface CustomerPortalLinkResponse {
  data: {
    url: string
  }
  success: boolean
}

export async function fetchCustomerPortalLink(): Promise<CustomerPortalLinkResponse> {
  try {
    const params = new URLSearchParams({
      return_url: window.location.origin,
    }).toString()
    const response = await apiBase.get<CustomerPortalLinkResponse>(
      `/v1/get-stripe-customer-portal-url?${params}`,
    )
    return response.data
  } catch (error) {
    console.error('Error fetching customer portal link:', error)
    throw error
  }
}

export interface SubscriptionPack {
  priceId: string
  productId: string
  name: string
  description: string
  price: number
  features: string
  interval: string
  units: number
  isHardware?: boolean
}

export async function fetchSubscriptionPacks(): Promise<{
  data: SubscriptionPack[]
}> {
  try {
    const response = await apiBase.get<{
      data: SubscriptionPack[]
    }>(`/v2/stripe/product-packs`)
    return response.data
  } catch (error) {
    console.error('Error fetching subscription packs:', error)
    throw error
  }
}
